<template>
  <GcBaseLayout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="baseView.mode"
    :backButtonEnable="backButtonEnable"
    :backButtonFunction="backButtonFunction"
    :formsConfig="formsConfig"
    :tabs="tabsAccessControl"
    :selectedIndex="screenConfig.selectedIndex"
  >
    <template #headerContent>
      <GcHeaderContentStatic
        :title="screenConfig.title"
        v-if="screenConfig.title"
      />
    </template>

    <div class="split-view-template css-v2 split-view">
      <gc-split-view
        :showLeftContainer="showLeftContainer"
        :showRightContainer="showRightContainer"
      >
        <template #leftContent>
          <div class="css-v2 split-view-list">
            <GcDataGrid
              :grouping="true"
              :export="true"
              :summary="true"
              :dataSourceConfig="screenConfig.gridData.gridOptions.dataSource"
              :columns="screenConfig.gridData.columns"
              :gridToolbar="screenConfig.gridData.toolbar"
              :padColumn="true"
              :id="'split-view-left-grid'"
              :rowClick="leftRowClickCallBack"
              :updateIsDefault="screenConfig.isDefaultEndPoint"
              v-if="dataLoaded"
              :exportFileName="screenConfig.exportFileName"
              :valueChangeListners="getLeftListners"
              :persistenceKey="persistenceKey"
              :heightPadding="20"
              :paging="false"
              ref="split-view-left-grid"
              :defaultUserSettings="defaultUserSettings"
            />
          </div>
        </template>

        <template #rightContent>
          <div class="right-panel-container" v-if="renderRightPanel">
            <gc-form-header-toolbar
              :toolbar-options="rightConfig.headerToolbar"
              :data-model="selectedLeftRecord"
              :backRoute="screenConfig.pageRoute"
              :formLocation="rightConfig.headerToolbar.toolbarFormLocation"
              :baseView="rightPanelBaseView"
              :closeCallBack="closeRightPanel"
              :itemSaveCallBack="handleSave"
              :key="toolbarKey"
            ></gc-form-header-toolbar>

            <!-- Selected Record Info Section -->
            <div class="selected-record-info">
              <GcForm
                :formOptions="selectedRecordFormOptions"
                :formItems="getFormItemsForMode()"
                :isNormal="true"
                :data-model="selectedLeftRecord"
                :formMode="rightPanelBaseView.mode"
                :formLocation="'normal'"
              />
            </div>

            <div class="view-card responsive-paddings split-view-right-grid">
              <GcDataGrid
                :grouping="rightConfig.gridData.grouping || false"
                :export="rightConfig.gridData.export || false"
                :summary="rightConfig.gridData.summary || false"
                :dataSourceConfig="rightGridDataSource"
                :columns="rightConfig.gridData.columns"
                :gridToolbar="rightConfig.gridData.toolbar"
                :padColumn="true"
                :id="'split-view-right-grid'"
                :rowClick="rightRowClickCallBack"
                v-if="rightGridDataLoaded"
                :exportFileName="rightConfig.gridData.exportFileName"
                :valueChangeListners="getRightListners"
                :heightPadding="rightConfig.gridData.heightPadding || 100"
                :paging="rightConfig.gridData.paging !== false"
                :gridOptions="rightGridOptions"
                ref="split-view-right-grid"
                :editingConfig="rightConfig.gridData.editingConfig"
              />
            </div>
          </div>
        </template>
      </gc-split-view>
    </div>
  </GcBaseLayout>
</template>

<script>
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import { SplitViewService } from "../_services/api/templates/split-view.services";
import { mapGetters } from "vuex";
import { tabAccessControl } from "../_helpers/access-control";

export default {
  props: {
    screenConfig: {
      type: Object,
      default: undefined,
    },
    rightConfig: {
      type: Object,
      default: undefined,
    },
    backButtonRoute: {
      type: String,
      default: "/home",
    },
    persistenceKey: {
      type: String,
    },
    backButtonEnable: {
      type: Boolean,
      default: true,
    },
    defaultUserSettings: {
      type: Boolean,
      default: true,
    },
  },
  mixins: [RouteMixin, FormMixin],
  components: {},

  data() {
    return {
      dataLoaded: true,
      selectedLeftRecord: null,
      pendingLeftRecord: null,
      renderRightPanel: false,
      rightGridDataSource: null,
      rightGridDataLoaded: false,
      selectedRecordFormOptions: {
        gcLabelLocation: "left",
        readOnly: true,
      },
      rightPanelBaseView: {
        mode: "review",
        id: "",
      },
      rightGridOptions: null,
      toolbarKey: 0,
    };
  },
  computed: {
    tabsAccessControl() {
      if (this.screenConfig.tabs)
        return tabAccessControl(this.screenConfig.tabs);
      else return [];
    },
    ...mapGetters({ updateIsDefault: "ui/getUpdateIsDefault" }),
    ...mapGetters({ updateIsDefaultCode: "ui/getUpdateIsDefaultCode" }),
    ...mapGetters({ displayServerErrorPopUp: "ui/getServerError" }),
    showLeftContainer() {
      return !(this.isXSmall && this.baseView.mode !== "");
    },
    showRightContainer() {
      return (
        this.baseView.mode === "create" ||
        !(this.baseView.mode == "review" && this.baseView.id == "")
      );
    },
    getLeftListners() {
      return this.screenConfig.gridData.valueChangedEventListners || [];
    },
    getRightListners() {
      return this.rightConfig.gridData.valueChangedEventListners || [];
    },
  },

  async created() {
    this.$store.dispatch("ui/headerFormHeight", undefined);

    // Build rightGridOptions with editing config and onEditorPreparing
    const gridOptions = {
      options: {}
    };

    // Add editing config if it exists
    if (this.rightConfig.gridData.editingConfig) {
      gridOptions.options.editing = this.rightConfig.gridData.editingConfig;
    }

    // Add onEditorPreparing if it exists
    if (this.rightConfig.gridData.onEditorPreparing) {
      gridOptions.options.onEditorPreparing = this.rightConfig.gridData.onEditorPreparing;
    }

    this.rightGridOptions = gridOptions;
  },

  methods: {
    getFormItemsForMode() {
      // Check if parent component has dynamic form items computed property
      if (this.$parent && this.$parent.selectedRecordFormItems) {
        return this.$parent.selectedRecordFormItems;
      }
    },

    backButtonFunction() {
      this.$router.push({
        path: this.backButtonRoute,
      });
    },

    async leftRowClickCallBack(e) {
      if (!e.data) return;

      // Store the clicked record data temporarily
      const clickedRecord = e.data;
      const idProperty =
        this.screenConfig.gridData.gridOptions.rowSelect.idProperty;
      const clickedRecordId = clickedRecord[idProperty];

      // Check if we're already on this record
      if (
        this.selectedLeftRecord &&
        this.selectedLeftRecord[idProperty] === clickedRecordId
      ) {
        // Same record clicked, do nothing
        return;
      }

      // Check if we're in edit OR create mode with potential unsaved changes
      if (
        (this.rightPanelBaseView.mode === "edit" ||
          this.rightPanelBaseView.mode === "create") &&
        this.selectedLeftRecord
      ) {
        // onRowClicked will handle navigation and show confirmation if needed
        // It will trigger a route change which will call handleLocalRouteChange
        this.onRowClicked(e, this.screenConfig.gridData.gridOptions.rowSelect);

        // Store the pending record to load after route change is confirmed
        this.pendingLeftRecord = clickedRecord;

        // Don't load data here - it will be loaded in handleLocalRouteChange
        // after the user confirms (or if there are no unsaved changes)
        return;
      }

      // If not in edit/create mode or no selected record, proceed normally
      this.onRowClicked(e, this.screenConfig.gridData.gridOptions.rowSelect);

      // Update the selected record - use direct assignment for reactivity
      this.selectedLeftRecord = clickedRecord;

      // Add computed boolean field for the switch if Inactive field exists
      if (this.selectedLeftRecord.Inactive !== undefined) {
        // Convert "Y"/"N" to boolean for the switch (Active = true when Inactive = "N")
        this.$set(
          this.selectedLeftRecord,
          "InactiveBoolean",
          this.selectedLeftRecord.Inactive === "N"
        );
      }

      // Increment key to force toolbar re-render with new data
      this.toolbarKey++;

      // Set right panel to review mode to show edit button
      this.rightPanelBaseView = {
        mode: "review",
        id: clickedRecordId || "",
      };

      // Load right grid data based on selected left record
      await this.loadRightGridData(clickedRecord);
    },

    async loadRightGridData(selectedRecord) {
      if (!this.rightConfig.gridData.dataSourceBuilder) {
        return;
      }

      this.showLoading(true);
      this.rightGridDataLoaded = false;

      try {
        // Build the data source configuration based on selected record
        this.rightGridDataSource =
          this.rightConfig.gridData.dataSourceBuilder(selectedRecord);

        this.renderRightPanel = true;
        this.rightGridDataLoaded = true;

        // Call custom callback if provided
        if (this.rightConfig.gridData.onLeftRecordSelected) {
          this.rightConfig.gridData.onLeftRecordSelected(selectedRecord);
        }
      } catch (err) {
        // console.log("Load right grid data error", err);
        // this.displayServerError(err, "Failed to load detail records");
      } finally {
        this.showLoading(false);
      }
    },

    rightRowClickCallBack(e) {
      if (!e.data) return;
    },

    handleSave() {
      if (this.rightConfig.headerToolbar.saveCallBack) {
        this.rightConfig.headerToolbar.saveCallBack();
      }
    },

    switchToEditMode() {
      if (!this.selectedLeftRecord) {
        // this.$notify("No record selected", "Warning");
        return;
      }

      const idProperty =
        this.screenConfig.gridData.gridOptions.rowSelect.idProperty;
      const recordId = this.selectedLeftRecord[idProperty];

      if (!recordId) {
        // this.$notify("Invalid record ID", "Error");
        return;
      }

      this.rightPanelBaseView = {
        mode: "edit",
        id: recordId,
      };
    },

    async handleLocalRouteChange() {
      this.renderRightPanel = false;
      this.rightGridDataLoaded = false;

      if (this.screenConfig.title) {
        document.title = this.screenConfig.title;
      }

      // Handle create mode
      if (this.baseView.mode === "create") {
        // If we have a pending record in create mode, user clicked cancel
        // Clear it and stay in create mode
        this.selectedLeftRecord = {}; // Initialize empty record for create mode
        this.pendingLeftRecord = null; // Clear pending record
        this.renderRightPanel = true;
        this.rightPanelBaseView = {
          mode: "create",
          id: "",
        };

        // Optionally load right grid with empty data or initialize it
        if (this.rightConfig.gridData.dataSourceBuilder) {
          // You might want to pass an empty object or handle this case in dataSourceBuilder
          this.rightGridDataSource =
            this.rightConfig.gridData.dataSourceBuilder({});
          this.rightGridDataLoaded = true;
        }
      }
      // Handle review/edit mode
      else if (
        (this.baseView.mode === "review" || this.baseView.mode === "edit") &&
        this.baseView.id
      ) {
        this.showLoading(true);

        // Check if we have a pending record (user clicked a different row while in edit/create mode)
        if (this.pendingLeftRecord) {
          const recordToLoad = this.pendingLeftRecord;
          this.pendingLeftRecord = null; // Clear pending record

          // Update the selected record
          this.selectedLeftRecord = recordToLoad;

          // Add computed boolean field for the switch if Inactive field exists
          if (this.selectedLeftRecord.Inactive !== undefined) {
            this.$set(
              this.selectedLeftRecord,
              "InactiveBoolean",
              this.selectedLeftRecord.Inactive === "N"
            );
          }

          // Increment key to force toolbar re-render
          this.toolbarKey++;

          // Set the panel base view
          this.rightPanelBaseView = {
            mode: this.baseView.mode,
            id: this.baseView.id,
          };

          await this.loadRightGridData(this.selectedLeftRecord);
        } else {
          // Normal flow - load from API
          await this.loadSelectedRecord();

          this.rightPanelBaseView = {
            mode: this.baseView.mode,
            id: this.baseView.id,
          };
        }
      } else {
        this.selectedLeftRecord = null;
        this.pendingLeftRecord = null;
        this.showLoading(false);
      }

      if (this.$refs["split-view-left-grid"]) {
        this.$refs["split-view-left-grid"].reSizeGrid();
      }

      if (this.$refs["split-view-right-grid"]) {
        this.$refs["split-view-right-grid"].reSizeGrid();
      }
    },

    async loadSelectedRecord() {
      var filter = undefined;
      const idProperty =
        this.screenConfig.gridData.gridOptions.rowSelect.idProperty;
      const idMode = this.screenConfig.gridData.gridOptions.rowSelect.idMode;

      if (idMode != "Guid" && idMode != "Number") {
        filter = `${idProperty} eq '${this.baseView.id}'`;
      } else {
        filter = `${idProperty} eq ${this.baseView.id}`;
      }

      try {
        const response = await SplitViewService.getByCode(
          this.screenConfig.recordEndPoint,
          filter
        );

        if (response.data.value.length > 0) {
          // Assign the data directly for reactivity
          this.selectedLeftRecord = response.data.value[0];

          // Add computed boolean field for the switch if Inactive field exists
          if (this.selectedLeftRecord.Inactive !== undefined) {
            // Convert "Y"/"N" to boolean for the switch (Active = true when Inactive = "N")
            this.$set(
              this.selectedLeftRecord,
              "InactiveBoolean",
              this.selectedLeftRecord.Inactive === "N"
            );
          }
          // Increment key to force toolbar re-render
          this.toolbarKey++;
          await this.loadRightGridData(this.selectedLeftRecord);
        }

        this.showLoading(false);
      } catch (err) {
        this.$logger.debug("Load selected record error", err);
        this.displayServerError(err, "Failed to load record");
        this.showLoading(false);
      }
    },

    refreshRightGrid() {
      if (this.selectedLeftRecord) {
        this.loadRightGridData(this.selectedLeftRecord);
      }
    },

    refreshLeftGrid() {
      if (this.$refs["split-view-left-grid"]) {
        this.$refs["split-view-left-grid"].gridRefresh(true);
      }
    },

    getRightGridInstance() {
      if (this.$refs["split-view-right-grid"]) {
        return this.$refs["split-view-right-grid"].getGridInstance();
      }
      return null;
    },

    closeRightPanel() {
      this.renderRightPanel = false;
      this.rightGridDataLoaded = false;
      this.selectedLeftRecord = null;
      this.rightPanelBaseView = {
        mode: "review",
        id: "",
      };

      // Clear the route to remove the id
      this.$router.push({
        path: this.screenConfig.pageRoute,
      });
    },
  },

  watch: {
    $route: {
      immediate: true,
      handler: "handleLocalRouteChange",
    },
    routeError(val) {
      if (val) this.showLoading(false);
      this.routeError = false;
    },
    displayServerErrorPopUp(val) {
      if (val) {
        this.displayServerError(val.error, val.title);
      }
    },
  },
};
</script>

<style lang="scss">
.responsive-paddings {
  padding: 20px;
}

.dx-scrollview-content .content .view-card.responsive-paddings {
  -webkit-box-shadow: none;
  box-shadow: none;
}

.split-view-template {
  .split-container .left-container {
    margin-right: 0;
    margin-left: 0;
    .dx-card {
      margin-left: 0;
    }
  }
  .split-container-child.right-container {
    margin-left: 0;
    padding-right: 0;
    box-sizing: border-box;
    overflow: hidden;
  }
}

.right-panel-container {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 85px);
  overflow: hidden;
}

.split-view-right-grid {
  flex: 1;
  overflow: auto;
  max-width: 100%;
  margin: 0;
  padding: 0 20px 20px 20px;

  // Remove any toolbar spacing
  .dx-datagrid-header-panel {
    display: none !important;
  }
}

.selected-record-info {
  background-color: #fff;
  padding: 15px 20px;
  margin: 0;
  border-bottom: 1px solid #e0e0e0;
  max-width: 100%;
  box-sizing: border-box;
  flex-shrink: 0;

  .dx-form {
    .dx-field-item {
      padding: 5px 0;
    }

    .dx-field-item-label {
      font-weight: 600;
    }
  }
}
</style>
